import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BanIcon, CigaretteOffIcon, ClockIcon, PawPrintIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { houseRules } from '../data/house';
import { brand, brandPillars, emblemStory, LOGO_URL } from '../data/brand';
const icons = {
  smoking: CigaretteOffIcon,
  pets: PawPrintIcon,
  party: BanIcon,
  shield: ShieldCheckIcon,
  clock: ClockIcon,
  users: UsersIcon
};
const palette = [{
  name: 'Bel-Air Green',
  meaning: 'Composure, exclusivity and retreat',
  className: 'bg-forest-700 text-ivory-100'
}, {
  name: 'Champagne Gold',
  meaning: 'Warmth, prestige and refinement',
  className: 'bg-gold-400 text-forest-800'
}, {
  name: 'Warm Ivory',
  meaning: 'Room to breathe',
  className: 'bg-ivory-50 text-forest-700 ring-1 ring-forest-700/10'
}];
export function About() {
  return <>
      <PageHeader eyebrow="About Bel Air" title="Structure, Strength and Prestige" intro={brand.positioning} data-id="element-325" data-mp="1uhs1gt9rgb74" />

      <section className="bg-ivory-100" data-id="element-326" data-mp="1pj8gow6wl5gc">
        <div className="mx-auto max-w-[1240px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28" data-id="element-327" data-mp="1pk9v9an4oa2t">
          <div className="grid items-center gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20" data-id="element-328" data-mp="23j9j4g2tgkxx">
            <Reveal className="flex justify-center rounded-sm bg-white p-10 shadow-soft sm:p-14" data-id="element-329" data-mp="1yibzdz25riub">
              <img src={LOGO_URL} alt="Bel Air Luxury Apartment logo — Redefined Luxury. Timeless Living." className="w-full max-w-[300px]" data-id="element-330" data-mp="2dfmc1bqjeurm" />
            </Reveal>
            <Reveal delay={0.06} data-id="element-331" data-mp="2m8pw42vou5v8">
              <p className="flex items-center gap-3 text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-332" data-mp="2m7wm8t1fn08v">
                <span className="h-px w-8 bg-gold-500" aria-hidden="true" data-id="element-333" data-mp="2i8nrplykiyc9" />
                The brand promise
              </p>
              <h2 className="mt-4 font-display text-3xl leading-tight text-forest-700 sm:text-[2.5rem]" data-id="element-334" data-mp="2w51bx2uhlduf">{brand.promise}</h2>
              <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-muted" data-id="element-335" data-mp="1oumojreqrgkj">
                {emblemStory.map(paragraph => <p key={paragraph} data-id="element-336" data-mp="1sx9ka3ej9ln0">{paragraph}</p>)}
                <p data-id="element-337" data-mp="1xut0v9ypeai4">
                  The bold monogram and elegant typography position Bel Air as more than a place to sleep — a recognisable
                  hospitality brand that communicates quality and distinction in every detail of your stay.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20 grid gap-8 md:grid-cols-3" data-id="element-338" data-mp="2cj61ik8qkhiy">
            {brandPillars.map((pillar, index) => <Reveal key={pillar.title} delay={index * 0.06} className="border-t border-gold-400 pt-6" data-id="element-339" data-mp="2gmc3hfotfmjm" data-mpi={index}>
                <h3 className="font-display text-2xl text-forest-700" data-id="element-340" data-mp="2lkdnq3xz9rwo">{pillar.title}</h3>
                <p className="mt-2.5 text-[15px] leading-relaxed text-muted" data-id="element-341" data-mp="m92ya03ayn76">{pillar.copy}</p>
              </Reveal>)}
          </div>

          <div className="mt-20 grid gap-4 sm:grid-cols-3" data-id="element-342" data-mp="jnd5veivpfdr">
            {palette.map((swatch, index) => <Reveal key={swatch.name} delay={index * 0.05} data-id="element-343" data-mp="ewwr9r3bnnpt" data-mpi={index}>
                <div className={`flex h-40 flex-col justify-end rounded-sm p-6 ${swatch.className}`} data-id="element-344" data-mp="euk0cofl630m">
                  <p className="font-display text-lg" data-id="element-345" data-mp="9vh7s3gslp9j">{swatch.name}</p>
                  <p className="mt-1 text-[13px] opacity-80" data-id="element-346" data-mp="4zs0tf2g1637">{swatch.meaning}</p>
                </div>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-ivory-200" data-id="element-347" data-mp="xmuvfw400wo6">
        <div className="mx-auto max-w-[1240px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28" data-id="element-348" data-mp="xl1pkj17mgh8">
          <Reveal data-id="element-349" data-mp="sm8baoe3mlg0">
            <p className="flex items-center gap-3 text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-350" data-mp="spw6rt33thxd">
              <span className="h-px w-8 bg-gold-500" aria-hidden="true" data-id="element-351" data-mp="oostnecfxzzu" />
              House rules
            </p>
            <h2 className="mt-4 max-w-xl font-display text-3xl leading-tight text-forest-700 sm:text-[2.6rem]" data-id="element-352" data-mp="39cx7pk08l6m8">
              The standards of your stay
            </h2>
          </Reveal>

          <ul className="mt-14 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-353" data-mp="15updin2h25aq">
            {houseRules.map((rule, index) => {
            const Icon = icons[rule.icon];
            return <Reveal as="li" key={rule.title} delay={index % 3 * 0.05} data-id="element-354" data-mp="w4mmgsewv1f1" data-mpi={index}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-forest-700 text-gold-300" data-id="element-355" data-mp="w2jlxeg4tpw2">
                    <Icon size={18} aria-hidden="true" data-id="element-356" data-mp="1ou3azezety5z" />
                  </span>
                  <h3 className="mt-5 font-display text-xl leading-snug text-forest-700" data-id="element-357" data-mp="1atsjqau6uhka">{rule.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted" data-id="element-358" data-mp="1evdtvofc8hqs">{rule.detail}</p>
                </Reveal>;
          })}
          </ul>

          <Reveal delay={0.12} data-id="element-359" data-mp="3vigbtm4ijsff">
            <div className="mt-16 flex flex-col gap-5 border-t border-forest-700/10 pt-10 sm:flex-row sm:items-center sm:justify-between" data-id="element-360" data-mp="3g8lq4wftqzs">
              <p className="max-w-lg text-[15px] leading-relaxed text-muted" data-id="element-361" data-mp="3s12cbls07ffr">
                Planning something outside these — a production crew or a late arrival? Clear it with us first.
              </p>
              <Link to="/contact" className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-362" data-mp="5tkk34rc5hi9">
                Contact us
                <ArrowRightIcon size={15} className="transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-363" data-mp="34dkdj6abnh3a" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>;
}